import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { toast } from 'sonner';

/**
 * Living Parts-Order Spreadsheet URL banner.
 *
 * A single app-wide setting (key: 'parts_order_sheet_url') stored in the
 * generic `settings` collection. Anyone can click the link when it's set;
 * only admins/techs see the "Edit link" affordance and can change it.
 * Empty state gives admins a "Set link" call-to-action; viewers/operators
 * see a soft "no link set yet" hint.
 */
const PartsOrderLinkBanner = ({ isAdmin }) => {
  const [url, setUrl] = useState('');
  const [savedUrl, setSavedUrl] = useState('');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/settings/parts_order_sheet_url`);
        const v = res.data?.value || '';
        setSavedUrl(v);
        setUrl(v);
      } catch {
        // non-fatal
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const normalize = (raw) => {
    const trimmed = (raw || '').trim();
    if (!trimmed) return '';
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    return `https://${trimmed}`;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const normalized = normalize(url);
      const res = await axios.put(`${API}/settings/parts_order_sheet_url`, { value: normalized });
      const stored = res.data?.value || '';
      setSavedUrl(stored);
      setUrl(stored);
      setEditing(false);
      toast.success(stored ? 'Parts-order link saved' : 'Parts-order link cleared');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save link');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  return (
    <div
      className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6 flex flex-wrap items-center gap-2"
      data-testid="parts-order-link-banner"
    >
      <span className="text-sm font-medium text-blue-900 shrink-0">
        📋 Parts Order Sheet:
      </span>

      {editing ? (
        <>
          <Input
            data-testid="parts-order-url-input"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://sharepoint.com/…/parts-order.xlsx"
            className="flex-1 min-w-[240px] bg-white"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); handleSave(); }
              if (e.key === 'Escape') { setEditing(false); setUrl(savedUrl); }
            }}
          />
          <Button size="sm" onClick={handleSave} disabled={saving} data-testid="parts-order-save-btn">
            {saving ? 'Saving…' : 'Save'}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => { setEditing(false); setUrl(savedUrl); }}
            disabled={saving}
            data-testid="parts-order-cancel-btn"
          >
            Cancel
          </Button>
        </>
      ) : savedUrl ? (
        <>
          <a
            href={savedUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-blue-700 hover:text-blue-900 underline break-all flex-1 min-w-0"
            data-testid="parts-order-link"
          >
            {savedUrl}
          </a>
          {isAdmin && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setEditing(true)}
              data-testid="parts-order-edit-btn"
            >
              Edit link
            </Button>
          )}
        </>
      ) : (
        <>
          <span className="text-sm text-slate-600 italic flex-1">
            {isAdmin ? 'Not set yet — paste your team\u2019s Excel/SharePoint URL.' : 'No link set yet — ask an admin.'}
          </span>
          {isAdmin && (
            <Button size="sm" onClick={() => setEditing(true)} data-testid="parts-order-set-btn">
              Set link
            </Button>
          )}
        </>
      )}
    </div>
  );
};

export default PartsOrderLinkBanner;
